import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";

const AverageBarGraph = ({
  id,
  data = [],
  categoryKey,
  barKeys = [],
  lineKeys = [],
  height = 400,
}) => {
  // 🔹 INTERNAL builder function
  const buildChartData = () => {
    if (!Array.isArray(data)) {
      return { categories: [], bars: [], lines: [] };
    }

    const categories = data.map((item) => item?.[categoryKey]);

    const bars = barKeys.map((bar) => ({
      name: bar.name,
      yAxisIndex: bar.yAxisIndex ?? 0,
      color: bar.color,
      unit: bar.unit,
      data: data.map((item) => Number(item?.[bar.key]) || 0),
    }));

    const lines = lineKeys.map((line) => ({
      name: line.name,
      yAxisIndex: line.yAxisIndex ?? 0,
      color: line.color,
      unit: line.unit,
      data: data.map((item) => Number(item?.[line.key]) || 0),
    }));

    return { categories, bars, lines };
  };

  const option = useMemo(() => {
    const { categories, bars, lines } = buildChartData();
    const allSeries = [...bars, ...lines];

    const yAxisCount =
      Math.max(...allSeries.map((s) => s.yAxisIndex ?? 0), 0) + 1;

    const yAxis = Array.from({ length: yAxisCount }).map((_, i) => {
      const series = allSeries.find((s) => s.yAxisIndex === i);
      return {
        type: "value",
        name: series?.name || "",
        position: i === 0 ? "left" : "right",
        offset: i > 1 ? (i - 1) * 60 : 0,
        alignTicks: true,
        axisLabel: {
          formatter: series?.unit ? `{value} ${series.unit}` : "{value}",
        },
        axisLine: {
          show: true,
          lineStyle: { color: series?.color },
        },
      };
    });

    return {
      tooltip: {
        trigger: "axis",
        axisPointer: { type: "cross" },
      },
      legend: {
        data: allSeries.map((s) => s.name),
      },
      grid: {
        right: yAxisCount > 1 ? 60 * yAxisCount : 40,
      },
      xAxis: {
        type: "category",
        data: categories,
      },
      yAxis,
      series: [
        ...bars.map((b) => ({
          name: b.name,
          type: "bar",
          data: b.data,
          yAxisIndex: b.yAxisIndex,
          itemStyle: b.color ? { color: b.color } : undefined,
        })),
        ...lines.map((l) => ({
          name: l.name,
          type: "line",
          smooth: true,
          data: l.data,
          yAxisIndex: l.yAxisIndex,
          lineStyle: l.color ? { color: l.color } : undefined,
          itemStyle: l.color ? { color: l.color } : undefined,
        })),
      ],
    };
  }, [data, categoryKey, barKeys, lineKeys]);

  return (
    <ReactECharts
      key={id}
      option={option}
      style={{ height, width: "100%" }}
      notMerge
      replaceMerge={["series", "xAxis", "yAxis"]}
    />
  );
};

export default AverageBarGraph;
